import { safeDifferenceInCalendarDays } from 'shared/util/datetime-fns';

import { type AccommodationState, type Facet, type Filters, type HotelOffer, KayakHotelProvider } from '@/interfaces';
import HotelPageSearchForm from './containers/hotelPageSearchForm/HotelPageSearchForm';
import { HotelOfferList } from './components/hotelOfferList/HotelOfferList';
import HotelPageFilter from './containers/hotelPageFilter/HotelPageFilter';
import displayValues from '@/assets/displayValues.json';
import { InfoBox } from '@/components/infoBox/InfoBox';
import { HotelOffersCarousel } from './components/hotelOfferCarousel/HotelOffersCarousel';
import { NoOffersWarning } from './components/noOffersWarning/NoOffersWarning';
import { useWindowSize } from '@/utils/WindowSizeProvider';
import { useSelector } from 'react-redux';

export type HotelOffersProps = {
    hotelId: number;
    directOffers: HotelOffer[];
    providerOffers: HotelOffer[];
    directIsPending: boolean;
    providersIsPending: boolean;
    countryKey: string;
    hotelKey: string;
};

const breakfastValue = displayValues.hotelPageCustomFilters.breakfastIncluded;

const isDirect = (offer: HotelOffer): boolean => offer.isDirect || offer.provider.code === KayakHotelProvider.REVATO;

const ascendingRateSortWithDirectPrio = (a: HotelOffer, b: HotelOffer) => {
    if (a.totalRate === b.totalRate && isDirect(a)) {
        return -1;
    }
    if (a.totalRate === b.totalRate && isDirect(b)) {
        return 1;
    }
    return a.totalRate - b.totalRate;
};

export const sortOffers = (directOffers: HotelOffer[], providerOffers: HotelOffer[]): HotelOffer[] =>
    directOffers.concat(providerOffers).sort(ascendingRateSortWithDirectPrio);

const filterOffers = (offers: HotelOffer[], filters: Filters): HotelOffer[] =>
    offers
        .filter((offer) => !filters.hotelPageCustom.breakfastIncluded || offer.inclusions.includes(breakfastValue))
        .filter((offer) => !filters.hotelPageCustom.freeCancellation || offer.hasFreeCancellation);

const validFilterFacets = (directOffers: HotelOffer[], providerOffers: HotelOffer[]): Facet[] => {
    const offers = directOffers.concat(providerOffers);
    return [
        {
            value: 'breakfastIncluded',
            displayValue: displayValues.hotelPageCustomFilters.breakfastIncluded,
            disabled: !offers.some((offer) => offer.inclusions.includes(breakfastValue)),
        },
        {
            value: 'freeCancellation',
            displayValue: displayValues.hotelPageCustomFilters.freeCancellation,
            disabled: !offers.some((offer) => offer.hasFreeCancellation),
        },
    ];
};

export const HotelOffers = ({
    hotelId,
    countryKey,
    hotelKey,
    directOffers,
    providerOffers,
    directIsPending,
    providersIsPending,
}: HotelOffersProps) => {
    const filters = useSelector((state: AccommodationState) => state.filters);
    const { isMobile } = useWindowSize();

    const isRevato = directOffers[0]?.provider?.code === KayakHotelProvider.REVATO;
    const extraDirectInfo =
        countryKey === 'Norway' && !isRevato ? (
            <InfoBox className="u-hide-lt768">
                Ved å bestille direkte hos hotellet får du flere fordeler, det blir færre mellomledd, og du trenger bare å forholde deg til
                hotellet. Smart for både deg og hotellet! Les mer&nbsp;
                <a
                    href="https://www.finn.no/reise/artikler/aktuelt/nyhet-finn-lanserer-egen-hotelltjeneste"
                    target="_blank"
                    rel="nofollow norefferer noreferrer">
                    her
                </a>
                .
            </InfoBox>
        ) : undefined;
    const directOffersFiltered = filterOffers(directOffers, filters);
    const providerOffersFiltered = filterOffers(providerOffers, filters);
    const offersByProvider = providerOffersFiltered.reduce((groupedOffers, offer) => {
        groupedOffers[offer.provider.code] = (groupedOffers[offer.provider.code] || []).concat(offer);
        return groupedOffers;
    }, {});
    const nights = safeDifferenceInCalendarDays(filters.checkInDate, filters.checkOutDate);
    const filterFacets = validFilterFacets(directOffers, providerOffers);
    const filtersSelected = Object.keys(filters.hotelPageCustom).filter((key) => filters.hotelPageCustom[key]).length > 0;
    return (
        <div className="u-border-bottom-solid" data-testid="hotelOffers">
            <h2 className="u-t3 u-strong u-pt32">Priser</h2>
            <HotelPageSearchForm hotelKey={hotelKey} hotelId={hotelId} />
            {(filters.checkInDate || filters.checkOutDate) && (
                <div id="offersList" className="u-mt16" data-testid="offersList">
                    <HotelPageFilter facetList={filterFacets} />
                </div>
            )}
            {isMobile ? (
                <>
                    {directOffersFiltered.length > 0 || directIsPending ? (
                        <HotelOffersCarousel
                            title={isRevato ? 'Book hos hotellets partner' : 'Book direkte hos hotellet'}
                            offers={directOffersFiltered}
                            rooms={filters.rooms}
                            nights={nights}
                            isDirect={true}
                            isPending={directIsPending}
                        />
                    ) : null}
                    {providersIsPending ? <HotelOffersCarousel offers={[]} rooms={filters.rooms} isPending /> : null}
                    {Object.keys(offersByProvider).map((providerCode) => (
                        <HotelOffersCarousel
                            key={providerCode}
                            offers={offersByProvider[providerCode]}
                            rooms={filters.rooms}
                            nights={nights}
                            isPending={providersIsPending}
                        />
                    ))}
                    {!directIsPending && !providersIsPending && directOffersFiltered.length === 0 && providerOffersFiltered.length === 0 ? (
                        <NoOffersWarning
                            checkInDate={filters.checkInDate}
                            checkOutDate={filters.checkOutDate}
                            filtersSelected={filtersSelected}
                        />
                    ) : null}
                </>
            ) : (
                <>
                    <HotelOfferList
                        id="directOfferList"
                        title={isRevato ? 'Book hos hotellets partner' : 'Book hos hotellet'}
                        extraInfo={extraDirectInfo}
                        className="u-ph0"
                        offers={directOffersFiltered}
                        rooms={filters.rooms}
                        nightsSearched={nights}
                        checkInDate={filters.checkInDate}
                        checkOutDate={filters.checkOutDate}
                        isPending={directIsPending}
                        filtersSelected={filtersSelected}
                        hideWhenEmpty={true}
                        isPrimary
                    />
                    <HotelOfferList
                        id="otaOfferList"
                        title="Book hos tilbydere"
                        className="u-ph0"
                        offers={providerOffersFiltered}
                        rooms={filters.rooms}
                        nightsSearched={nights}
                        checkInDate={filters.checkInDate}
                        checkOutDate={filters.checkOutDate}
                        isPending={providersIsPending}
                        filtersSelected={Object.keys(filters.hotelPageCustom).filter((key) => filters.hotelPageCustom[key]).length > 0}
                        hideWhenEmpty={directOffersFiltered.length !== 0}
                        defaultOffersShown={4}
                    />
                </>
            )}
        </div>
    );
};
